import {
  FieldPath, deleteField, doc, getDocFromServer, onSnapshot, runTransaction, updateDoc,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { ouvirVigiado } from '@/lib/listenerVigiado';
import { GRADE_SUGERIDA } from '@/agenda/grade';

const clinicaOuNulo = (snap) => (snap.exists() ? { id: snap.id, ...snap.data() } : null);

/**
 * ★★★ WU-2/P3+P7 — UM documento que manda em expediente, feriados, origens, regras da
 * captação e nos textos que vão para o paciente.
 *
 * ⚠⚠ Era o segundo pior da lista de "o que pode ficar velho sem ninguém perceber"
 * (`docs/wu-reincidencia-1610.md` §7): um retrato velho daqui muda a tela inteira, e **nada**
 * no sistema vigiava. ★ Agora a 1ª emissão vinda do cache é conferida com o servidor.
 */
export function ouvirClinica(clinicaId, aoReceber, aoFalhar, aoDesatualizar) {
  const ref = doc(db, 'clinicas', clinicaId);
  return ouvirVigiado({
    assunto: 'a configuração da clínica',
    assinar: (aoSnap, aoErro) => onSnapshot(
      ref,
      { includeMetadataChanges: true },
      (snap) => aoSnap(clinicaOuNulo(snap), snap.metadata.fromCache),
      aoErro,
    ),
    doServidor: async () => clinicaOuNulo(await getDocFromServer(ref)),
    aoReceber,
    aoFalhar,
    aoDesatualizar,
  });
}

/**
 * Só o gestor consegue gravar — as rules recusam os demais papéis.
 *
 * ⚠ SUBSTITUI O `config` INTEIRO. Hoje não tem chamador; quem for usar precisa
 * mandar o objeto completo, senão apaga `funil`, `horarioAtendimento` e o que
 * mais estiver lá. Para mexer num campo só, use `atualizarCampoConfig`.
 */
export async function atualizarConfig(clinicaId, config) {
  await updateDoc(doc(db, 'clinicas', clinicaId), { config });
}

/**
 * Muda UM campo de `config`, preservando os vizinhos.
 *
 * O caminho pontilhado é o que torna a escrita cirúrgica: `config` guarda o
 * funil customizado e o horário de atendimento (que a saudação automática lê),
 * e gravar o objeto inteiro para trocar um booleano apagaria os dois.
 *
 * As rules permitem: a lista de bloqueio do update da clínica nomeia
 * `planoId`, `limites`, `ativa` e `criadaEm` — `config` não está lá.
 */
export async function atualizarCampoConfig(clinicaId, campo, valor) {
  await updateDoc(doc(db, 'clinicas', clinicaId), { [`config.${campo}`]: valor });
}


/**
 * ★★★ Feriado sem meta (02/11/2026, P1) — GRAVA UMA DATA DA AGENDA, NA HORA, e só ela.
 *
 * `transformar(atual)` recebe a exceção daquela data COMO ESTÁ NO SERVIDOR (lida dentro da
 * transação) e devolve a nova, ou `null` para apagar — ver `agenda/excecoes.js`. ⚠ Nunca a
 * cópia da tela: duas abas abertas não se desfazem uma à outra.
 *
 * ★ `FieldPath`, e não caminho pontilhado: a chave tem hífen ('2026-11-02').
 *
 * ⚠⚠⚠ A ARMADILHA QUE JÁ FECHOU A CLÍNICA: numa clínica SEM `agendaAvaliacao`, a agenda roda pela
 * grade sugerida. Gravar só `excecoes` criaria `agendaAvaliacao = { excecoes }`, SEM `dias` — e
 * dia sem horários é dia FECHADO. A semana inteira fecharia por um bloqueio de um dia. ★ Então,
 * nesse caso, os `dias` da grade sugerida (a mesma que a agenda já usava) vão juntos.
 *
 * @returns a exceção gravada (ou `null`, se apagou)
 */
export async function alterarExcecaoDaAgenda(clinicaId, data, transformar) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(data ?? ''))) throw new Error(`data inválida: ${data}`);
  const ref = doc(db, 'clinicas', clinicaId);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('clínica não encontrada');
    const agenda = snap.data()?.config?.agendaAvaliacao;
    const temAgenda = Boolean(agenda) && typeof agenda === 'object';
    const atual = temAgenda ? agenda.excecoes?.[data] : undefined;
    const nova = transformar(atual);
    const campo = new FieldPath('config', 'agendaAvaliacao', 'excecoes', data);
    const valor = nova === null ? deleteField() : nova;
    if (temAgenda) tx.update(ref, campo, valor);
    else tx.update(ref, campo, valor, new FieldPath('config', 'agendaAvaliacao', 'dias'), GRADE_SUGERIDA.dias);
    return nova;
  });
}

/**
 * ★★★ "SALVAR GRADE" — só os dias da semana e as recorrências. ⚠⚠ NUNCA `excecoes`.
 *
 * ⚠ Antes a tela gravava o objeto `agendaAvaliacao` inteiro (`atualizarCampoConfig`), com as
 * exceções da TELA. Com bloquear/feriado gravando na hora, uma cópia velha na tela (ou noutra
 * aba) apagaria o que acabou de ser gravado. ★ Os vizinhos de `config` e as `excecoes` ficam
 * como estão no servidor.
 */
export async function salvarGradeDaAgenda(clinicaId, { dias, recorrencias }) {
  await updateDoc(doc(db, 'clinicas', clinicaId), {
    'config.agendaAvaliacao.dias': dias,
    'config.agendaAvaliacao.recorrencias': recorrencias,
  });
}
